import React, { useEffect, useMemo, useState } from "react";
import { getDashboardUser } from "../../api/Dashboard";
import { toast } from "react-toastify";

type InfoItem = {
    href?: string;
    title: string;
    value: string | number;
    icon: string;
};

const STATIC_INFO_ITEMS: InfoItem[] = [
    {
        title: "Tiết kiệm",
        value: "0đ",
        icon: "https://cdn-gcs.thuocsi.vn/thuocsi-live/images/202414a515576e016ecc9ac6dcd1d575",
    },
];

type DashboardStats = {
    revenue: number;
    total_products: number;
    total_orders: number;
    total_wishlist_products: number;
    total_viewed_products: number;
};

function formatCurrency(value: number) {
    return `${new Intl.NumberFormat("vi-VN").format(value)}đ`;
}

export default function DashboardOverview() {
    const now = new Date();;

    const [dashboardStats, setDashboardStats] = useState<DashboardStats>({
        revenue: 0,
        total_products: 0,
        total_orders: 0,
        total_wishlist_products: 0,
        total_viewed_products: 0,
    });

    useEffect(() => {
        let isMounted = true;

        const fetchDashboardStats = async () => {
            try {
                const response = await getDashboardUser();

                if (!isMounted || !response?.status) {
                    return;
                }

                setDashboardStats({
                    revenue: response.data?.revenue ?? 0,
                    total_products: response.data?.total_products ?? 0,
                    total_orders: response.data?.total_orders ?? 0,
                    total_wishlist_products: response.data?.total_wishlist_products ?? 0,
                    total_viewed_products: response.data?.total_viewed_products ?? 0,
                });
            } catch (error) {
                console.error("Error loading dashboard overview:", error);
            }
        };

        fetchDashboardStats();

        return () => {
            isMounted = false;
        };
    }, []);

    const infoItems = useMemo<InfoItem[]>(() => {
        return [
            {
                href: "/my-order",
                title: `Doanh số`,
                value: formatCurrency(dashboardStats.revenue),
                icon: "https://cdn-gcs.thuocsi.vn/thuocsi-live/images/2024b2885326be5e814a2ad4e3af0c8a",
            },
            {
                href: "/history-purchased",
                title: `SP đã mua`,
                value: dashboardStats.total_products,
                icon: "https://cdn-gcs.thuocsi.vn/thuocsi-live/images/20249b6461a5838c5b348314032a0032",
            },
            {
                href: "/my-order",
                title: "Đơn hàng",
                value: dashboardStats.total_orders,
                icon: "https://cdn-gcs.thuocsi.vn/thuocsi-live/images/20245c45b72c9233a554593f8e3956f2",
            },
            ...STATIC_INFO_ITEMS,
            {
                href: "/user/wishlist",
                title: "Quan tâm",
                value: dashboardStats.total_wishlist_products,
                icon: "https://cdn-gcs.thuocsi.vn/thuocsi-live/images/20245f6512ec4932f3edb2e6bc22a1ed",
            },
            {
                href: "/productviewed",
                title: "Đã xem",
                value: dashboardStats.total_viewed_products,
                icon: "https://cdn-gcs.thuocsi.vn/thuocsi-live/images/202497b951c46f2af0357308333c5c95",
            },
        ];
    }, [dashboardStats]);

    return (
        <div id="BLOCK_OVERVIEW" className="grid md:gap-4 gap-0 grid-cols-2 md:!grid-cols-5">

            {/* LEFT */}
            <div
                className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 p-4 rounded-[10px] col-span-3 bg-gradient"
            >
                {infoItems.map((item, idx) => (
                    <InfoBox key={idx} {...item} />
                ))}
            </div>

            {/* RIGHT */}
            <div className="max-h-full min-h-[248px] col-span-2 mt-4 md:!mt-0">
                <div
                    className="rounded-xl h-full bg-gradient"
                >
                    <div className="p-2 px-6 h-full flex flex-col items-center justify-center gap-4 text-center">
                        <p className="text-white">
                            Bạn chưa có đơn hàng nào trong bốn tháng gần nhất.
                            Đặt hàng ngay để hưởng những ưu đãi hấp dẫn!
                        </p>
                        <a href="/quick-order" target="_blank">
                            <button className="px-4 py-2 bg-gradient text-white rounded-md">
                                Đặt Hàng Ngay
                            </button>
                        </a>
                    </div>
                </div>
            </div>

        </div>
    );
}

/* ================= INFO BOX (GIỮ NGUYÊN DOM) ================= */

function InfoBox({ href, title, value, icon }: InfoItem) {
    const Content = (
        <div className="InfoBox_container__oRgh9">
            <div className="InfoBox_iconContainer__t8gHO">
                <span style={{ position: "relative", display: "inline-block" }}>
                    <span>
                        <img
                            aria-hidden
                            src="data:image/svg+xml,%3csvg xmlns='http://www.w3.org/2000/svg' width='50' height='50'/%3e"
                        />
                    </span>
                    <img
                        src={icon}
                        alt="icon"
                        style={{
                            position: "absolute",
                            inset: 0,
                            width: "100%",
                            height: "100%",
                            objectFit: "contain",
                        }}
                    />
                </span>
            </div>

            <div className="InfoBox_contentContainer__Swe6A">
                <h3 className="InfoBox_title__LLcEP">{title}</h3>
                <span className="InfoBox_data__8C5WE">{value}</span>
            </div>
        </div>
    );

    return href ? (
        <div onClick={(e) => {
            e.stopPropagation()
            const token = localStorage.getItem('token');
            if (!token) {
                toast.error('Bạn cần đăng nhập để truy cập trang này.', {
                    position: "bottom-right",
                });
                return;
            }
            window.open(href, "_blank")
        }}>{Content}</div>
    ) : (
        Content
    );
}
